import { AlertCircle, RefreshCw, X } from 'lucide-react';

type ErrorVariant = 'default' | 'inline' | 'banner';

interface ErrorMessageProps {
  message: string;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  variant?: ErrorVariant;
  className?: string;
}

/**
 * Reusable error message component with multiple variants
 * Best practices:
 * - Clear error icon for visual identification
 * - Optional retry action for recoverable errors
 * - Optional dismiss for non-critical errors
 * - Accessible with proper ARIA role
 */
export function ErrorMessage({
  message,
  title = 'Error',
  onRetry,
  onDismiss,
  variant = 'default',
  className = '',
}: ErrorMessageProps) {
  if (variant === 'inline') {
    return (
      <p className={`text-sm text-red-600 flex items-center gap-1 ${className}`} role="alert">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        {message}
      </p>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        className={`bg-red-50 border-l-4 border-red-500 p-4 ${className}`}
        role="alert"
      >
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-red-800">{title}</p>
            <p className="text-sm text-red-700 mt-1">{message}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:text-red-800"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            )}
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="ml-3 text-red-400 hover:text-red-600"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div
      className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-800">{title}</p>
          <p className="text-sm text-red-600 mt-1">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-red-400 hover:text-red-600 p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Alert component for non-error messages (info, warning, success)
 */
type AlertType = 'info' | 'warning' | 'success';

const alertStyles: Record<AlertType, { bg: string; border: string; icon: string; title: string; text: string }> = {
  info: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    icon: 'text-sky-500',
    title: 'text-sky-800',
    text: 'text-sky-600',
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-500',
    title: 'text-yellow-800',
    text: 'text-yellow-600',
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-500',
    title: 'text-green-800',
    text: 'text-green-600',
  },
};

interface AlertProps {
  type: AlertType;
  title?: string;
  message: string;
  onDismiss?: () => void;
  className?: string;
}

export function Alert({ type, title, message, onDismiss, className = '' }: AlertProps) {
  const styles = alertStyles[type];

  return (
    <div
      className={`${styles.bg} border ${styles.border} rounded-lg p-4 ${className}`}
      role="status"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className={`w-5 h-5 ${styles.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          {title && <p className={`text-sm font-medium ${styles.title}`}>{title}</p>}
          <p className={`text-sm ${styles.text} ${title ? 'mt-1' : ''}`}>{message}</p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`${styles.icon} hover:opacity-70 p-1`}
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
